import { motion, Variants } from "framer-motion";
import { ComponentProps, ReactNode, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import {
  selectMenuModal,
  setModal,
} from "../../../data-access/store/ui/uiSlice";
import Menu from "../../../../menu/feature/menu";
import { StatsChip, StatsChipsEnum } from "../../stats-chip";
import { Player } from "../../player";
import "./navbar.scss";
import {
  selectToken,
  selectUser,
} from "../../../../auth/data-access/store/authSlice";
import {
  selectCanInstallPwa,
  selectLanguage,
  selectTranslations,
} from "../../../data-access/store/general/generalSlice";
import { checkTranslationKey } from "../../../../utils/translation";
// import SoundManager from "../../../../utils/managers/sound-manager";
import { Link } from "react-router-dom";
import { Modal } from "../../modal";

export interface NavBarProps extends ComponentProps<"div"> {
  navbarType?: "standard" | "full";
  hideMenu?: boolean;
  hasStats?: boolean;
  hasAvatar?: boolean;
  slotEnd?: "level" | "lives" | ReactNode;
  slotBeforeLogo?: ReactNode;
}

const menuVariants: Variants = {
  hidden: {
    y: "-100%",
  },
  visible: {
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
};

export function NavBar({
  navbarType = "standard",
  children,
  hideMenu,
  hasStats,
  hasAvatar,
  slotEnd,
  slotBeforeLogo,
  ...props
}: NavBarProps) {
  const dispatch = useAppDispatch();
  const menuModal = useAppSelector(selectMenuModal);
  const user = useAppSelector(selectUser);
  const lang = useAppSelector(selectLanguage);
  const translations = useAppSelector(selectTranslations);
  const token = useAppSelector(selectToken);
  const isGuest = token === process.env.REACT_APP_GUEST_KEY;
  const canInstallPwa = useAppSelector(selectCanInstallPwa);
  const [showInstallPwaModal, setShowInstallPwaModal] = useState(false);
  const isIos = /iphone|ipad|ipod/.test(
    window.navigator.userAgent.toLowerCase()
  );

  // const [livesChanged, setLivesChanged] = useState<boolean>(false);
  // const isInitialMount = useRef(true);
  // useEffect(() => {
  //   if (isInitialMount.current) {
  //     isInitialMount.current = false;
  //   } else if (!isInitialMount.current && user?.character.skills.lives) {
  //     setLivesChanged(true);
  //     SoundManager.vfxSounds.life.play();
  //
  //     setTimeout(() => {
  //       setLivesChanged(false);
  //     }, 800);
  //   }
  // }, [user?.character.skills.lives]);

  const slotEndChip = (value: string | ReactNode) => {
    switch (value) {
      case "lives":
        return (
          <StatsChip
            type={StatsChipsEnum.LIVES}
            value={isGuest ? "∞" : user?.character.skills.lives}
          />
        );
      case "level":
        return (
          <StatsChip
            type={StatsChipsEnum.LEVEL}
            value={user?.character.level.current}
          />
        );
      default:
        return value;
    }
  };

  return (
    <>
      <motion.div
        id="navbar"
        variants={menuVariants}
        initial="hidden"
        animate="visible"
      >
        <div
          {...props}
          className={`menu menu-${navbarType} ${props.className ?? ""} ${
            hasStats ? "has-stats" : ""
          } ${slotBeforeLogo ? "has-slot-before-logo" : ""}`}
        >
          <div className="slot-start">
            {!hideMenu && !isGuest ? (
              <img
                src="/img/menu.png"
                alt={checkTranslationKey(translations[lang]?.Menu, "Menu")}
                width="30px"
                onClick={() => {
                  dispatch(setModal({ modal: "menu", value: true }));
                }}
              />
            ) : null}
          </div>
          {slotBeforeLogo && (
            <div className="slot-before-logo">{slotBeforeLogo}</div>
          )}
          <div className="slot-logo">
            <img
              src="/img/batteup-logo-2x.png"
              alt={checkTranslationKey(
                translations[lang]?.BattleUp_Logo,
                "BattleUp Logo"
              )}
              className="logo"
            />
          </div>
          <div className="slot-end">
            {canInstallPwa && (
              <img
                src="img/icns/download.png"
                alt=""
                className="download-pwa"
                onClick={() => {
                  setShowInstallPwaModal(true);
                }}
              />
            )}
            {slotEnd ? (
              slotEndChip(slotEnd)
            ) : hasAvatar ? (
              <Link to="/profile/edit" className="avatar-container">
                <Player
                  hasShadow={false}
                  avatar={user?.character.avatar}
                  level={user?.character?.level?.current}
                />
              </Link>
            ) : null}
            {/*<AnimatePresence>*/}
            {/*  {livesChanged && (*/}
            {/*    <motion.span*/}
            {/*      transition={{ duration: 0.8 }}*/}
            {/*      initial={{ opacity: 0 }}*/}
            {/*      animate={{ opacity: 1 }}*/}
            {/*      exit={{ opacity: 0 }}*/}
            {/*      className={`minus-life ${livesChanged ? "active" : ""}`}*/}
            {/*    >*/}
            {/*      -1*/}
            {/*    </motion.span>*/}
            {/*  )}*/}
            {/*</AnimatePresence>*/}
          </div>
        </div>
        {navbarType === "full" ? (
          <div className="rewards-area">{children}</div>
        ) : null}
        {hasStats && user ? (
          <div className="stats-header">
            <StatsChip
              type={StatsChipsEnum.LEVEL}
              value={user?.character.level.current}
            />
            <StatsChip
              type={StatsChipsEnum.POINTS}
              value={user?.character.points}
            />
            <StatsChip
              type={StatsChipsEnum.COINS}
              value={
                user?.eligibility.credits.purchased +
                user?.eligibility.credits.subscription
              }
            />
            <StatsChip
              type={StatsChipsEnum.LIVES}
              value={isGuest ? "∞" : user?.character.skills.lives}
            />
          </div>
        ) : null}
      </motion.div>

      <div className={`modal-menu ${menuModal ? "open" : ""}`}>
        <Menu />
      </div>

      {canInstallPwa && (
        <Modal show={showInstallPwaModal} setShow={setShowInstallPwaModal}>
          <div className="modal-content install-pwa-content">
            <p className="title-modal">
              {checkTranslationKey(
                translations[lang]?.Add_to_Home_Screen,
                "Add the BattleUp app to your home screen"
              )}
            </p>
            <div className="modal-description">
              <p className="subtitle">
                {checkTranslationKey(
                  translations[lang]?.Add_to_Home_Screen_Subtitle,
                  "4 easy steps:"
                )}
              </p>
              <div className="steps">
                {isIos ? (
                  <>
                    <div className="step">
                      <p
                        dangerouslySetInnerHTML={{
                          __html: checkTranslationKey(
                            translations[lang]?.Add_to_Home_Screen_ios_Step_1,
                            "Tap the <strong>“Share”</strong> icon at the bottom of the browser"
                          ),
                        }}
                      ></p>
                      <img
                        src="img/steps-pwa/ios/step-1.png"
                        alt=""
                        width={243}
                      />
                    </div>
                    <div className="step">
                      <p
                        dangerouslySetInnerHTML={{
                          __html: checkTranslationKey(
                            translations[lang]?.Add_to_Home_Screen_ios_Step_2,
                            "Tap <strong>“Add”</strong> to place <strong>BattleUp</strong> on your home screen"
                          ),
                        }}
                      ></p>
                      <img
                        src="img/steps-pwa/ios/step-2.png"
                        alt=""
                        width={187}
                      />
                    </div>
                    <div className="step">
                      <p
                        dangerouslySetInnerHTML={{
                          __html: checkTranslationKey(
                            translations[lang]?.Add_to_Home_Screen_ios_Step_3,
                            "Tap <strong>“Add”</strong> to place BattleUp on your home screen"
                          ),
                        }}
                      ></p>
                      <img
                        src="img/steps-pwa/ios/step-3.png"
                        alt=""
                        width={220}
                      />
                    </div>
                    <div className="step">
                      <p
                        dangerouslySetInnerHTML={{
                          __html: checkTranslationKey(
                            translations[lang]?.Add_to_Home_Screen_ios_Step_4,
                            "Place the icon where you want, tap <strong>BattleUp</strong> and enjoy easy access."
                          ),
                        }}
                      ></p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="step">
                      <p
                        dangerouslySetInnerHTML={{
                          __html: checkTranslationKey(
                            translations[lang]
                              ?.Add_to_Home_Screen_Android_Step_1,
                            "Tap the <strong>“Menu”</strong> icon (three dots in the top-right)"
                          ),
                        }}
                      ></p>
                      <img
                        src="img/steps-pwa/android/step-1.png"
                        alt=""
                        width={243}
                      />
                    </div>
                    <div className="step">
                      <p
                        dangerouslySetInnerHTML={{
                          __html: checkTranslationKey(
                            translations[lang]
                              ?.Add_to_Home_Screen_Android_Step_2,
                            "Select <strong>“Add to Home Screen”</strong>"
                          ),
                        }}
                      ></p>
                      <img
                        src="img/steps-pwa/android/step-2.png"
                        alt=""
                        width={187}
                      />
                    </div>
                    <div className="step">
                      <p
                        dangerouslySetInnerHTML={{
                          __html: checkTranslationKey(
                            translations[lang]
                              ?.Add_to_Home_Screen_Android_Step_3,
                            "Tap <strong>“Add”</strong> to place BattleUp on your home screen"
                          ),
                        }}
                      ></p>
                      <img
                        src="img/steps-pwa/android/step-3.png"
                        alt=""
                        width={220}
                      />
                    </div>
                    <div className="step">
                      <p
                        dangerouslySetInnerHTML={{
                          __html: checkTranslationKey(
                            translations[lang]
                              ?.Add_to_Home_Screen_Android_Step_4,
                            "Place the icon where you want, tap <strong>BattleUp</strong> and enjoy easy access."
                          ),
                        }}
                      ></p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

export default NavBar;
